import { clearToken, getToken } from "./auth";
import { data as dataRouter } from "react-router";

type Method = RequestInit["method"];

const getInitDefault = (method: Method, body?: any) => {
  const init: RequestInit = {
    method,
    headers: {
      Accept: "application/json",
    },
  };
  const token = getToken();
  if (token && init?.headers) {
    init.headers = {
      ...init.headers,
      Authorization: `Bearer ${token}`,
    };
  }
  if (!(body instanceof FormData)) {
    init.headers = {
      ...init.headers,
      "Content-Type": "application/json",
    };
  }
  return init;
};

const getUrl = (url: string | URL) => {
  if (url instanceof URL) {
    url.hostname = import.meta.env.VITE_API_URL;
    return url;
  }
  while (url.startsWith("/")) {
    url = url.slice(1);
  }
  return `${import.meta.env.VITE_API_URL}/${url}`;
};

const parseResponse = async <T = any>(res: Response) => {
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      clearToken();
      window.location.href = "/login";
      throw dataRouter("Unauthorized", { status: res.status });
    }
    if (res.status >= 399) {
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const dataRes = await res.json();
        throw dataRouter(dataRes.message, { status: res.status, ...dataRes });
      }
      const text = await res.text();
      throw dataRouter(text || `Error ${res.status}`, { status: res.status });
    }
    throw new Error(`HTTP ${res.status}`);
  }
  const contentType = res.headers.get("content-type") || "";
  const data = contentType.includes("application/json")
    ? await res.json()
    : ((await res.text()) as any);
  return data as T;
};

function createMethod<T>(method: Method) {
  return async (url: string | URL, body?: any): Promise<T> => {
    const init = getInitDefault(method, body);
    if (body) {
      if (body instanceof FormData) {
        init.body = body;
      } else {
        init.body = JSON.stringify(body);
      }
    }
    const res = await fetch(getUrl(url), init);
    return parseResponse<T>(res);
  };
}

export const api = {
  get<T = any>(url: string | URL): Promise<T> {
    return createMethod<T>("GET")(url);
  },
  delete<T = any>(url: string | URL): Promise<T> {
    return createMethod<T>("DELETE")(url);
  },
  post<T = any>(url: string | URL, body?: any): Promise<T> {
    return createMethod<T>("POST")(url, body);
  },
  patch<T = any>(url: string | URL, body?: any): Promise<T> {
    return createMethod<T>("PATCH")(url, body);
  },
  put<T = any>(url: string | URL, body?: any): Promise<T> {
    return createMethod<T>("PUT")(url, body);
  },
};
